
import { Unit, Transaction, UserProfile } from './types';
import { INITIAL_UNITS } from './constants';
import { createClient } from '@supabase/supabase-js';

const getEnvVar = (key: string): string => {
  try {
    // @ts-ignore
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env[key] || '';
    }
  } catch (e) {
    console.warn(`Could not access environment variable ${key}:`, e);
  }
  return '';
};

const SB_URL = getEnvVar('VITE_SUPABASE_URL');
const SB_KEY = getEnvVar('VITE_SUPABASE_ANON_KEY');

export const supabase = (SB_URL && SB_KEY) ? createClient(SB_URL, SB_KEY) : null;

const STORAGE_KEY_UNITS = 'mh_units';
const STORAGE_KEY_TRANSACTIONS = 'mh_transactions';
const STORAGE_KEY_PROFILE = 'mh_current_profile';

// --- Improved Async Persistence Logic ---

export const loadUnits = async (): Promise<Unit[]> => {
  // 1. Try to load from Supabase first
  if (supabase) {
    const { data, error } = await supabase.from('units').select('*');
    if (!error && data && data.length > 0) return data as Unit[];
  }

  // 2. Fallback to LocalStorage (Legacy Data)
  try {
    const stored = localStorage.getItem(STORAGE_KEY_UNITS);
    if (stored && stored !== '[]' && stored !== 'undefined') {
      const localData = JSON.parse(stored);
      // If we have Supabase, let's "Up-sync" the local data now
      if (supabase && localData.length > 0) {
        await supabase.from('units').upsert(localData);
      }
      return localData;
    }
  } catch (e) {
    console.error("Error loading units:", e);
  }

  return INITIAL_UNITS;
};

export const saveUnits = async (units: Unit[]) => {
  // Save to LocalStorage for offline/redundancy
  localStorage.setItem(STORAGE_KEY_UNITS, JSON.stringify(units));
  
  // Save to Supabase for persistence across devices
  if (supabase) {
    const { error } = await supabase.from('units').upsert(units);
    if (error) console.error("Supabase sync error (units):", error);
  }
};

export const loadTransactions = async (): Promise<Transaction[]> => {
  if (supabase) {
    const { data, error } = await supabase.from('transactions').select('*');
    if (!error && data) return data as Transaction[];
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY_TRANSACTIONS);
    const localData = (stored && stored !== 'undefined') ? JSON.parse(stored) : [];
    if (supabase && localData.length > 0) {
        await supabase.from('transactions').upsert(localData);
    }
    return localData;
  } catch (e) {
    return [];
  }
};

export const saveTransactions = async (txs: Transaction[]) => {
  localStorage.setItem(STORAGE_KEY_TRANSACTIONS, JSON.stringify(txs));
  if (supabase) {
    const { error } = await supabase.from('transactions').upsert(txs);
    if (error) console.error("Supabase sync error (txs):", error);
  }
};

export const loadCurrentProfile = (): UserProfile => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PROFILE);
    if (stored && stored !== 'undefined') {
        return JSON.parse(stored);
    }
    return {
      id: 'admin-1',
      name: 'Admin User',
      email: 'admin@categoryone.com',
      role: 'Owner',
      phone: '(555) 123-4567',
      joinedDate: '2023-01-10'
    };
  } catch (e) {
    return { id: 'admin-1', name: 'Admin User', email: '', role: 'Owner', phone: '', joinedDate: '' };
  }
};

export const saveCurrentProfile = (profile: UserProfile) => {
  localStorage.setItem(STORAGE_KEY_PROFILE, JSON.stringify(profile));
};
